import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getUser, updateUser, changeUserPassword, updateUserStatus, removeUser, uploadUserPhoto, API_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { ArrowLeft, Save, Loader2, Shield, Key, Trash2, Camera, Check, XCircle, User, Mail, Phone, Briefcase, Building2, Palette } from 'lucide-react';

const userTypes = { 0: 'God', 5: 'Admin', 6: 'Operations', 1: 'Employee', 2: 'Contractor', 3: 'Sub Contractor' };
const typeColors = { 0: 'bg-red-100 text-red-700 border-red-200', 5: 'bg-purple-100 text-purple-700 border-purple-200', 6: 'bg-cyan-100 text-cyan-700 border-cyan-200', 1: 'bg-blue-100 text-blue-700 border-blue-200', 2: 'bg-orange-100 text-orange-700 border-orange-200', 3: 'bg-slate-100 text-slate-700 border-slate-200' };

const COLOR_PALETTE = [
  { value: '#4dbd74', label: 'Green' },
  { value: '#1E88E5', label: 'Blue' },
  { value: '#E53935', label: 'Red' },
  { value: '#8E24AA', label: 'Purple' },
  { value: '#F57C00', label: 'Orange' },
  { value: '#00ACC1', label: 'Cyan' },
  { value: '#EC407A', label: 'Pink' },
  { value: '#FDD835', label: 'Yellow' },
  { value: '#6D4C41', label: 'Brown' },
  { value: '#546E7A', label: 'Steel' },
];

function StaffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const isGod = currentUser?.user_type === 0;
  const isSelf = currentUser?.id === id;
  const canEditAccount = isGod || isSelf;          // God edits anyone; users edit themselves
  const canChangePassword = isGod || isSelf;       // God resets anyone's; users change own
  const canToggleStatus = isGod && !isSelf;        // God only; and not self
  const canRemove = isGod && !isSelf;              // God only; and not self
  const [editing, setEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [form, setForm] = useState({});
  const [newPassword, setNewPassword] = useState('');

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => getUser(id).then(r => r.data),
  });

  // Sync form state with fetched user data
  useEffect(() => {
    if (user && !editing) {
      setForm(user);
    }
  }, [user, editing]);

  const updateMutation = useMutation({
    mutationFn: (data) => updateUser(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['user', id] }); queryClient.invalidateQueries({ queryKey: ['users'] }); setEditing(false); toast.success('User updated'); }
  });

  const passwordMutation = useMutation({
    mutationFn: (data) => changeUserPassword(id, data),
    onSuccess: () => { setShowPasswordModal(false); setNewPassword(''); toast.success('Password changed'); }
  });

  const statusMutation = useMutation({
    mutationFn: (data) => updateUserStatus(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['user', id] }); queryClient.invalidateQueries({ queryKey: ['users'] }); toast.success('Status updated'); }
  });

  const removeMutation = useMutation({
    mutationFn: () => removeUser(id),
    onSuccess: () => { toast.success('User removed'); navigate('/staff'); }
  });

  const photoMutation = useMutation({
    mutationFn: (formData) => uploadUserPhoto(id, formData),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['user', id] }); toast.success('Photo updated'); }
  });

  const startEdit = () => { setForm({ ...user }); setEditing(true); };
  const cancelEdit = () => { setForm({ ...user }); setEditing(false); };
  const saveEdit = () => {
    updateMutation.mutate({
      first_name: form.first_name, last_name: form.last_name,
      email: form.email, phone: form.phone,
      designation: form.designation, contractor_business_name: form.contractor_business_name,
      user_type: form.user_type, color_code: form.color_code || '',
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    photoMutation.mutate(fd);
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!user) return <div className="text-center py-20 text-slate-500">User not found</div>;

  const isActive = user.is_active !== false;
  const fullName = user.user_type === 2 && user.contractor_business_name ? user.contractor_business_name : `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const photoUrl = user.image_url ? `${API_URL}/api/users/${id}/photo?t=${user.updated_at || ''}` : null;

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl" data-testid="staff-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/staff')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-staff-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800 font-heading">User Account</h1>
          <p className="text-slate-500 mt-0.5">Manage user details and settings</p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-card border" data-testid="user-profile-card">
        <div className="p-6 border-b bg-slate-50/50">
          <div className="flex items-start gap-5">
            {/* Avatar with photo upload */}
            <div className="relative group">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-semibold overflow-hidden">
                {photoUrl ? (
                  <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  fullName.charAt(0) || 'U'
                )}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity" data-testid="upload-photo-btn">
                <Camera size={20} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </label>
              {photoMutation.isPending && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                  <Loader2 size={20} className="text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-800">{fullName}</h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${typeColors[user.user_type]}`}>
                  {userTypes[user.user_type]}
                </span>
                {isActive ? (
                  <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><Check size={14} />Active</span>
                ) : (
                  <span className="flex items-center gap-1 text-red-600 text-xs font-medium"><XCircle size={14} />Inactive</span>
                )}
              </div>
              <p className="text-slate-500 mt-1">@{user.username}</p>
              {user.designation && <p className="text-sm text-slate-500 mt-0.5">{user.designation}</p>}
            </div>
            <div className="flex gap-2">
              {!editing ? (
                canEditAccount && (
                  <button onClick={startEdit} className="px-4 py-2 bg-primary text-white text-sm rounded-sm hover:bg-primary/90" data-testid="edit-user-btn">Edit</button>
                )
              ) : (
                <>
                  <button onClick={cancelEdit} className="px-4 py-2 border text-sm rounded-sm hover:bg-slate-50" data-testid="cancel-edit-btn">Cancel</button>
                  <button onClick={saveEdit} disabled={updateMutation.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-sm hover:bg-primary/90 flex items-center gap-1.5" data-testid="save-user-btn">
                    {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* User Details Form/View */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field icon={User} label="First Name" value={form.first_name} editing={editing} onChange={(v) => setForm({...form, first_name: v})} testId="field-first-name" />
            <Field icon={User} label="Last Name" value={form.last_name} editing={editing} onChange={(v) => setForm({...form, last_name: v})} testId="field-last-name" />
            <Field icon={Mail} label="Email" value={form.email} editing={editing} onChange={(v) => setForm({...form, email: v})} type="email" testId="field-email" />
            <Field icon={Phone} label="Phone" value={form.phone} editing={editing} onChange={(v) => setForm({...form, phone: v})} testId="field-phone" />
            <Field icon={Briefcase} label="Designation" value={form.designation} editing={editing} onChange={(v) => setForm({...form, designation: v})} testId="field-designation" />
            {editing && isGod ? (
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  <div className="flex items-center gap-2"><Shield size={14} className="text-slate-400" />User Type</div>
                </label>
                <select value={form.user_type ?? ''} onChange={(e) => setForm({...form, user_type: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-sm text-sm" data-testid="field-user-type">
                  {Object.entries(userTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  <div className="flex items-center gap-2"><Shield size={14} className="text-slate-400" />User Type</div>
                </label>
                <p className="text-sm text-slate-800 py-2">{userTypes[user.user_type]}</p>
              </div>
            )}
            {(user.user_type === 2 || (editing && form.user_type === 2)) && (
              <Field icon={Building2} label="Business Name" value={form.contractor_business_name} editing={editing} onChange={(v) => setForm({...form, contractor_business_name: v})} testId="field-business-name" className="md:col-span-2" />
            )}
            {/* Colour Code Picker */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-600 mb-1.5">
                <div className="flex items-center gap-2"><Palette size={14} className="text-slate-400" />Calendar Colour</div>
              </label>
              {editing ? (
                <div className="flex flex-wrap gap-2" data-testid="color-picker">
                  {COLOR_PALETTE.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setForm({...form, color_code: c.value})}
                      className={`w-9 h-9 rounded-full border-2 transition-all flex items-center justify-center ${form.color_code === c.value ? 'border-slate-800 scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                      data-testid={`color-${c.label.toLowerCase()}`}
                    >
                      {form.color_code === c.value && <Check size={16} className="text-white drop-shadow" />}
                    </button>
                  ))}
                  {form.color_code && (
                    <button type="button" onClick={() => setForm({...form, color_code: ''})} className="text-xs text-slate-400 hover:text-slate-600 ml-2 self-center" data-testid="clear-color-btn">Clear</button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 py-2">
                  {user.color_code ? (
                    <>
                      <span className="w-7 h-7 rounded-full border" style={{ backgroundColor: user.color_code }} data-testid="user-color-display" />
                      <span className="text-sm text-slate-600">{COLOR_PALETTE.find(c => c.value === user.color_code)?.label || user.color_code}</span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400">No colour assigned</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      {(canChangePassword || canToggleStatus || canRemove) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Change Password */}
          {canChangePassword && (
            <button onClick={() => setShowPasswordModal(true)} className="bg-white rounded-card border p-5 text-left hover:border-primary/40 hover:shadow-sm transition-all group" data-testid="change-password-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors"><Key size={18} className="text-amber-600" /></div>
                <div><p className="font-medium text-slate-800">Change Password</p><p className="text-xs text-slate-500">{isSelf ? 'Set a new password for your account' : "Reset this user's password"}</p></div>
              </div>
            </button>
          )}

          {/* Toggle Active Status */}
          {canToggleStatus && (
            <button onClick={() => statusMutation.mutate({ is_active: !isActive })} className="bg-white rounded-card border p-5 text-left hover:border-primary/40 hover:shadow-sm transition-all group" data-testid="toggle-status-card">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-red-50 group-hover:bg-red-100' : 'bg-green-50 group-hover:bg-green-100'}`}>
                  {isActive ? <XCircle size={18} className="text-red-600" /> : <Check size={18} className="text-green-600" />}
                </div>
                <div><p className="font-medium text-slate-800">{isActive ? 'Deactivate' : 'Activate'} Account</p><p className="text-xs text-slate-500">{isActive ? 'Disable user login' : 'Re-enable user login'}</p></div>
              </div>
            </button>
          )}

          {/* Remove User */}
          {canRemove && (
            <button onClick={() => setShowRemoveConfirm(true)} className="bg-white rounded-card border p-5 text-left hover:border-red-300 hover:shadow-sm transition-all group" data-testid="remove-user-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors"><Trash2 size={18} className="text-red-600" /></div>
                <div><p className="font-medium text-slate-800">Remove User</p><p className="text-xs text-slate-500">Permanently remove this account</p></div>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Account Info */}
      <div className="bg-white rounded-card border p-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Account Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-slate-500">Username</span><p className="font-medium mt-0.5">{user.username}</p></div>
          <div><span className="text-slate-500">Created</span><p className="font-medium mt-0.5">{user.created_at ? formatDate(user.created_at) : '-'}</p></div>
          <div><span className="text-slate-500">Last Updated</span><p className="font-medium mt-0.5">{user.updated_at ? formatDate(user.updated_at) : '-'}</p></div>
          <div><span className="text-slate-500">Status</span><p className="font-medium mt-0.5">{isActive ? 'Active' : 'Inactive'}</p></div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-card w-full max-w-sm">
            <div className="p-5 border-b"><h3 className="text-lg font-semibold font-heading">Change Password</h3><p className="text-sm text-slate-500 mt-1">Set a new password for {fullName}</p></div>
            <div className="p-5">
              <label className="block text-sm font-medium mb-1.5">New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="w-full px-3 py-2 border rounded-sm" data-testid="new-password-input" />
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => { setShowPasswordModal(false); setNewPassword(''); }} className="px-4 py-2 border text-sm rounded-sm" data-testid="cancel-password-btn">Cancel</button>
              <button onClick={() => passwordMutation.mutate({ new_password: newPassword })} disabled={!newPassword || newPassword.length < 4 || passwordMutation.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="save-password-btn">
                {passwordMutation.isPending && <Loader2 size={14} className="animate-spin" />} Change Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirm Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-card w-full max-w-sm">
            <div className="p-5 border-b"><h3 className="text-lg font-semibold text-red-600">Remove User</h3></div>
            <div className="p-5"><p className="text-sm text-slate-600">Are you sure you want to remove <strong>{fullName}</strong>? This action cannot be undone.</p></div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setShowRemoveConfirm(false)} className="px-4 py-2 border text-sm rounded-sm" data-testid="cancel-remove-btn">Cancel</button>
              <button onClick={() => removeMutation.mutate()} disabled={removeMutation.isPending} className="px-4 py-2 bg-red-600 text-white text-sm rounded-sm flex items-center gap-1.5" data-testid="confirm-remove-btn">
                {removeMutation.isPending && <Loader2 size={14} className="animate-spin" />} Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value, editing, onChange, type = 'text', testId, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-600 mb-1.5">
        <div className="flex items-center gap-2">{Icon && <Icon size={14} className="text-slate-400" />}{label}</div>
      </label>
      {editing ? (
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded-sm text-sm" data-testid={testId} />
      ) : (
        <p className="text-sm text-slate-800 py-2" data-testid={testId}>{value || '-'}</p>
      )}
    </div>
  );
}

export default StaffDetail;
